import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Booking, BookingStatus } from '../../../core/models/entities/booking';
import { BookingsService } from '../../../core/services/bookings.service';
import { UnlockDoorService } from '../../../core/services/unlock-door.service';
import { LocalizedUrlService } from '../../../core/services/localized-url.service';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { uiActions } from 'angular/app/core/store/ui';
import { parseISO } from 'date-fns';
import { CancelConditionsHelper } from '../../../core/helpers/cancel-conditions.helper';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-booking-detail-page',
  template: `
    <div class="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      @if (loading()) {
        <div class="mt-40 flex justify-center">
          <app-loader></app-loader>
        </div>
      } @else {
        @if (!booking()) {
          <div>
            <app-empty-state
              [showAddButton]="false"
              [message]="'my-booking-page.no-booking-found' | transloco"
            ></app-empty-state>
          </div>
        }

        @if (booking()) {
          <!-- Header Card -->
          <div
            class="mb-6 rounded-xl border border-gray-200 bg-white shadow-sm"
          >
            <div class="border-b border-gray-200 p-6 sm:p-8">
              <h2>{{ 'my-booking-page.title' | transloco }}</h2>
              @if (booking()?.reference) {
                <p class="mt-1"> {{ 'my-booking-page.reference' | transloco: { ref: booking()!.reference } }} </p>
              }
              <div class="mt-4">
                <app-badge [variant]="getStatusVariant(booking()!.status)">
                  {{ getStatusLabel(booking()!.status) }}
                </app-badge>
              </div>
            </div>

            <div class="p-6 sm:p-8">
              <!-- Booking Information -->
              <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
                <div class="space-y-3">
                  <div class="font-bold"> {{ 'my-booking-page.date-time' | transloco }} </div>
                  <div class="space-y-1">
                    @for (line of booking()!.description_lines; track line) {
                      <p>{{ line }}</p>
                    }
                  </div>
                </div>

                <div class="space-y-3">
                  <div class="font-bold">{{ 'my-booking-page.room' | transloco }}</div>
                  <div>
                    <p class="font-medium">{{ booking()?.room?.name }}</p>
                    <a
                      class="inline-flex items-center gap-x-1 font-medium decoration-2 hover:underline"
                      [href]="roomUrl()"
                      target="_blank"
                    >
                      {{ 'my-booking-page.see-room-details' | transloco }}
                    </a>
                    <i class="far fa-external-link ml-2 text-xs"></i>
                  </div>
                </div>
              </div>

              <!-- Door Unlock Section intégrée -->
              @if (booking()?.unlock_door_url) {
                <hr class="my-6" />
                <div>
                  <div class="mb-4">
                    <app-button
                      [loading]="unlockingDoor()"
                      variant="primary"
                      size="sm"
                      [disabled]="!isUnlockWindowActive(booking())"
                      (click)="unlockDoor()"
                    >
                      {{ 'my-booking-page.open-door' | transloco }}
                    </app-button>
                  </div>
                  @if (booking()?.unlock_door_window_humanized) {
                    <p class="text-sm text-gray-600">
                      {{ 'my-booking-page.door-window' | transloco: { before: booking()!.unlock_door_window_humanized, after: '' } }}
                    </p>
                    <p class="text-sm text-gray-600">
                      {{ 'my-booking-page.door-link-invalid' | transloco }}
                    </p>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Invoices Section -->
          @if (booking()?.invoices && booking()!.invoices.length > 0) {
            <div
              class="mb-6 rounded-xl border border-gray-200 bg-white shadow-sm"
            >
              <div class="p-6 sm:p-8">
                <app-customer-invoices-table
                  [booking]="booking()!"
                  [invoices]="booking()!.invoices"
                >
                </app-customer-invoices-table>
              </div>
            </div>
          }

          <!-- Cancellation Section -->
          @if (isCancellable()) {
            <div class="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div class="p-6 sm:p-8">
                <app-booking-cancellation-section
                  [booking]="booking()!"
                  [token]="token()"
                  (bookingCancelled)="onBookingCancelled()"
                ></app-booking-cancellation-section>
              </div>
            </div>
          }

          <!-- Cancellation Section — réservation récurrente -->
          @if (isRecurringCancellable()) {
            <div class="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div class="p-6 sm:p-8">
                <h3 class="mb-4">{{ 'my-booking-page.cancellation-conditions' | transloco }}</h3>
                @for (line of cancelConditionsSummaryLines(); track line) {
                  <div class="mb-2">{{ line }}</div>
                }
                @if (booking()?.room?.vendor?.general_terms_public_url) {
                  <a
                    class="mt-4 block underline"
                    [href]="booking()!.room!.vendor!.general_terms_public_url"
                    target="_blank"
                  >
                    {{ 'my-booking-page.see-general-terms' | transloco }}
                  </a>
                }
                <p class="mt-4">
                  {{ 'my-booking-page.recurrent-cancel-info' | transloco }}
                </p>
                <div class="mt-6">
                  <a
                    [href]="'mailto:' + (booking()!.room!.admin_email || booking()!.room!.vendor!.email)"
                    class="inline-flex items-center justify-center gap-x-2 rounded-lg border bg-gray-800 px-3 py-2 text-sm font-medium text-white no-underline transition-all hover:bg-gray-900 focus:bg-gray-900 focus:outline-none"
                  >
                    {{ 'my-booking-page.contact-host' | transloco }}
                  </a>
                </div>
              </div>
            </div>
          }
        }
      }
    </div>
  `,
  standalone: false,
})
export class MyBookingPage implements OnInit {
  private route = inject(ActivatedRoute);
  private bookingsService = inject(BookingsService);
  private unlockDoorService = inject(UnlockDoorService);
  private store = inject(Store);
  private translocoService = inject(TranslocoService);
  private localizedUrlService = inject(LocalizedUrlService);

  loading = signal(true);
  booking = signal<Booking | null>(null);
  token = signal<string>('');
  unlockingDoor = signal(false);
  BookingStatus = BookingStatus;

  roomUrl = computed(() => {
    const currentBooking = this.booking();
    if (!currentBooking) return '';
    return this.localizedUrlService
      .link('room', {
        vendorSlug: currentBooking.room?.vendor?.slug ?? '',
        id: currentBooking.room_id,
      })
      .join('/');
  });
  isCancellable = computed(() => {
    const currentBooking = this.booking();
    if (!currentBooking) return false;
    return (
      (currentBooking.status === BookingStatus.Confirmed ||
        currentBooking.status === BookingStatus.Pending) &&
      !this.isBookingPast() &&
      !currentBooking.recurrence_rule
    );
  });

  isRecurringCancellable = computed(() => {
    const currentBooking = this.booking();
    if (!currentBooking) return false;
    return (
      (currentBooking.status === BookingStatus.Confirmed ||
        currentBooking.status === BookingStatus.Pending) &&
      !this.isBookingPast() &&
      !!currentBooking.recurrence_rule
    );
  });

  cancelConditionsSummaryLines = computed(() =>
    CancelConditionsHelper.getCancelConditionLines(
      this.booking()?.room?.vendor?.cancel_conditions ?? [],
      this.translocoService,
    ),
  );

  private isBookingPast = computed(() => {
    const currentBooking = this.booking();
    if (!currentBooking?.start) return false;
    const bookingStart = parseISO(currentBooking.start);
    const now = new Date();
    return bookingStart < now;
  });

  async ngOnInit() {
    this.token.set(this.route.snapshot.params['token']);
    await this.loadBookingByToken();
  }

  private async loadBookingByToken() {
    try {
      const bookingData = (
        await firstValueFrom(this.bookingsService.findByToken(this.token()))
      ).data;
      this.booking.set(bookingData);
      this.store.dispatch(
        uiActions.setVendor({ vendor: bookingData.room.vendor }),
      );
    } catch (e) {}
    this.loading.set(false);
  }

  async onBookingCancelled() {
    // Afficher le loader et vider le booking pendant le rechargement
    this.loading.set(true);
    this.booking.set(null);

    // Recharger la réservation pour afficher le nouveau statut
    await this.loadBookingByToken();
  }

  isUnlockWindowActive(booking: Booking): boolean {
    if (
      !booking?.unlock_door_window_start ||
      !booking?.unlock_door_window_end
    ) {
      return false;
    }

    const now = new Date();
    const unlockStart = new Date(booking.unlock_door_window_start);
    const unlockEnd = new Date(booking.unlock_door_window_end);

    return now >= unlockStart && now <= unlockEnd;
  }

  async unlockDoor() {
    const currentToken = this.token();
    if (!currentToken) return;

    this.unlockingDoor.set(true);
    const success = await this.unlockDoorService.unlockDoor(currentToken);
    this.unlockingDoor.set(false);
  }

  getStatusLabel(status: BookingStatus): string {
    switch (status) {
      case BookingStatus.Confirmed:
        return this.translocoService.translate('my-booking-page.status.confirmed');
      case BookingStatus.Pending:
        return this.translocoService.translate('my-booking-page.status.pending');
      case BookingStatus.Cancelled:
        return this.translocoService.translate('my-booking-page.status.cancelled');
      case BookingStatus.Refused:
        return this.translocoService.translate('my-booking-page.status.refused');
      default:
        return this.translocoService.translate('my-booking-page.status.unknown');
    }
  }

  getStatusVariant(
    status: BookingStatus,
  ): 'success' | 'secondary' | 'danger' | 'outlined' {
    switch (status) {
      case BookingStatus.Confirmed:
        return 'success';
      case BookingStatus.Pending:
        return 'outlined';
      case BookingStatus.Cancelled:
      case BookingStatus.Refused:
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
